/**
 * JSON Import - Utility Functions
 *
 * Parses, validates, and writes imported JSON data to localStorage.
 * Implements "lenient import" strategy: clamp numerics, use defaults for missing.
 * Strict on enum values — reject unknown values.
 */

import type { SimulatorExportData, ImportResult } from '../types';
import { EXPORT_VERSION, STORAGE_KEYS } from '../types';
import type { FuelPrice } from '../heating-cost';
import { getDefaultFuelPrices, getDefaultInvestmentCosts } from '../heating-cost';
import { DEFAULT_PREFERENCES } from '../hooks/useLocalStorage';
import {
  DEFAULT_SETTINGS,
  STORAGE_KEY_PRICES,
  STORAGE_KEY_INVESTMENT,
  STORAGE_KEY_SETTINGS,
} from '../heating-cost/hooks/useHeatingCostConfig';

// =============================================================================
// ENUM VALIDATORS
// =============================================================================

const VALID_BUILDING_PERIODS = [
  'before_1945', '1945_1966', '1967_1985', '1986_1998',
  '1999_2008', '2009_2013', '2014_2020', '2021_plus',
] as const;

const VALID_BUILDING_TYPES = [
  'detached', 'semi_detached', 'terraced', 'apartment',
] as const;

const VALID_RENOVATION_STATUSES = ['none', 'partial', 'full'] as const;

const VALID_INSULATION_LEVELS = ['none', 'partial', 'full', 'modern'] as const;

const VALID_WINDOW_TYPES = ['single', 'double_old', 'double_new', 'triple'] as const;

const VALID_HEATING_SYSTEMS = [
  'gas_condensing', 'gas_traditional', 'coal', 'biomass',
  'heat_pump_air', 'heat_pump_ground', 'electric_radiators',
  'electric_floor', 'district_heating', 'stove',
] as const;

const VALID_SYSTEM_AGES = ['new', 'medium', 'old'] as const;

const VALID_HOT_WATER_SOURCES = [
  'combined', 'electric', 'gas_instant', 'solar',
] as const;

const VALID_RENEWABLE_SOURCES = [
  'solar_thermal', 'recuperation', 'ground_exchanger',
] as const;

// =============================================================================
// HELPER: validate enum field
// =============================================================================

function validateEnum(
  value: unknown,
  validValues: readonly string[],
  fieldName: string,
  errors: string[]
): boolean {
  if (typeof value !== 'string' || !validValues.includes(value)) {
    errors.push(`Nieprawidłowa wartość ${fieldName}: "${String(value)}"`);
    return false;
  }
  return true;
}

// =============================================================================
// HELPER: clamp numeric field
// =============================================================================

function clampNumeric(
  value: unknown,
  min: number,
  max: number,
  fieldName: string,
  warnings: string[]
): number {
  const num = typeof value === 'number' ? value : Number(value);
  if (isNaN(num)) {
    warnings.push(`${fieldName}: nieprawidłowa wartość, ustawiono ${min}`);
    return min;
  }
  if (num < min) {
    warnings.push(`${fieldName}: wartość ${num} poniżej minimum (${min}), ustawiono ${min}`);
    return min;
  }
  if (num > max) {
    warnings.push(`${fieldName}: wartość ${num} powyżej maksimum (${max}), ustawiono ${max}`);
    return max;
  }
  return num;
}

// =============================================================================
// validateExportData
// =============================================================================

export function validateExportData(raw: unknown): ImportResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Level 1: basic structure
  if (raw === null || raw === undefined || typeof raw !== 'object' || Array.isArray(raw)) {
    return { success: false, errors: ['Dane muszą być obiektem JSON'], warnings: [] };
  }

  const obj = raw as Record<string, unknown>;

  // Version check (backward compat)
  let version = EXPORT_VERSION;
  if (!obj.version) {
    warnings.push('Brak pola version — przyjęto 1.0');
  } else {
    version = String(obj.version);
  }

  // formState — REQUIRED
  if (!obj.formState || typeof obj.formState !== 'object') {
    errors.push('Brak wymaganego pola formState');
    return { success: false, errors, warnings };
  }

  const fs = obj.formState as Record<string, unknown>;

  // Level 3: validate enums in formState
  validateEnum(fs.buildingPeriod, VALID_BUILDING_PERIODS, 'buildingPeriod', errors);
  validateEnum(fs.buildingType, VALID_BUILDING_TYPES, 'buildingType', errors);
  validateEnum(fs.renovationStatus, VALID_RENOVATION_STATUSES, 'renovationStatus', errors);
  validateEnum(fs.wallInsulation, VALID_INSULATION_LEVELS, 'wallInsulation', errors);
  validateEnum(fs.roofInsulation, VALID_INSULATION_LEVELS, 'roofInsulation', errors);
  validateEnum(fs.windowType, VALID_WINDOW_TYPES, 'windowType', errors);
  validateEnum(fs.heatingSystem, VALID_HEATING_SYSTEMS, 'heatingSystem', errors);
  validateEnum(fs.systemAge, VALID_SYSTEM_AGES, 'systemAge', errors);
  validateEnum(fs.hotWaterSource, VALID_HOT_WATER_SOURCES, 'hotWaterSource', errors);

  // Validate optional secondary heating enum if present
  if (fs.hasSecondaryHeating && fs.secondaryHeatingSystem) {
    validateEnum(fs.secondaryHeatingSystem, VALID_HEATING_SYSTEMS, 'secondaryHeatingSystem', errors);
  }

  // Validate renewables array
  if (Array.isArray(fs.renewables)) {
    for (const r of fs.renewables) {
      validateEnum(r, VALID_RENEWABLE_SOURCES, 'renewables', errors);
    }
  }

  // If enum errors found, abort
  if (errors.length > 0) {
    return { success: false, errors, warnings };
  }

  // Level 4: clamp numeric ranges
  fs.usableArea = clampNumeric(fs.usableArea, 20, 500, 'usableArea', warnings);
  fs.floors = clampNumeric(fs.floors, 1, 10, 'floors', warnings);
  fs.occupants = clampNumeric(fs.occupants, 1, 20, 'occupants', warnings);

  if (fs.pvPower !== undefined && fs.pvPower !== null) {
    fs.pvPower = clampNumeric(fs.pvPower, 0.1, 100, 'pvPower', warnings);
  }
  if (fs.secondaryHeatingShare !== undefined && fs.secondaryHeatingShare !== null) {
    fs.secondaryHeatingShare = clampNumeric(fs.secondaryHeatingShare, 10, 50, 'secondaryHeatingShare', warnings);
  }

  // savedSimulations — optional, defaults to []
  const savedSimulations = Array.isArray(obj.savedSimulations) ? obj.savedSimulations : [];
  if (!obj.savedSimulations) {
    // No warning needed — empty is fine
  }

  // preferences — optional, defaults to DEFAULT_PREFERENCES
  let preferences = DEFAULT_PREFERENCES;
  if (obj.preferences && typeof obj.preferences === 'object') {
    preferences = { ...DEFAULT_PREFERENCES, ...(obj.preferences as Record<string, unknown>) } as typeof preferences;
  } else if (obj.preferences === undefined) {
    warnings.push('Brak pola preferences — użyto domyślnych');
  }

  // heatingCost — optional, defaults to system defaults
  let heatingCost: SimulatorExportData['heatingCost'];
  if (obj.heatingCost && typeof obj.heatingCost === 'object') {
    const hc = obj.heatingCost as Record<string, unknown>;
    heatingCost = {
      fuelPrices: Array.isArray(hc.fuelPrices) ? hc.fuelPrices as SimulatorExportData['heatingCost']['fuelPrices'] : getDefaultFuelPrices(),
      investmentCosts: Array.isArray(hc.investmentCosts) ? hc.investmentCosts as SimulatorExportData['heatingCost']['investmentCosts'] : getDefaultInvestmentCosts(),
      settings: (hc.settings && typeof hc.settings === 'object')
        ? { ...DEFAULT_SETTINGS, ...(hc.settings as Record<string, unknown>) } as typeof DEFAULT_SETTINGS
        : DEFAULT_SETTINGS,
    };
  } else {
    warnings.push('Brak pola heatingCost — użyto domyślnych');
    heatingCost = {
      fuelPrices: getDefaultFuelPrices(),
      investmentCosts: getDefaultInvestmentCosts(),
      settings: { ...DEFAULT_SETTINGS },
    };
  }

  const data: SimulatorExportData = {
    version,
    exportedAt: typeof obj.exportedAt === 'string' ? obj.exportedAt : new Date().toISOString(),
    formState: fs as unknown as SimulatorExportData['formState'],
    savedSimulations: savedSimulations as SimulatorExportData['savedSimulations'],
    preferences,
    heatingCost,
  };

  return { success: true, data, warnings, errors: [] };
}

// =============================================================================
// mergeFuelPrices
// =============================================================================

/**
 * Merges imported fuel prices with current defaults.
 * - User-modified prices are preserved from import.
 * - Non-modified prices use current defaults (may have updated pricing).
 * - New variants (not in import) get defaults.
 * - Imported variants not in current defaults are dropped.
 */
export function mergeFuelPrices(
  imported: FuelPrice[],
  currentDefaults: FuelPrice[]
): FuelPrice[] {
  return currentDefaults.map(defaultPrice => {
    const importedPrice = imported.find(p => p.variantId === defaultPrice.variantId);
    if (importedPrice?.isUserModified) {
      return importedPrice;
    }
    return defaultPrice;
  });
}

// =============================================================================
// writeToLocalStorage
// =============================================================================

/**
 * Writes all validated export data to the 6 localStorage keys.
 * Fuel prices are merged with current defaults (user-modified preserved).
 */
export function writeToLocalStorage(data: SimulatorExportData): void {
  // Simulator state (3 keys)
  localStorage.setItem(STORAGE_KEYS.FORM_STATE, JSON.stringify(data.formState));
  localStorage.setItem(STORAGE_KEYS.SAVED_SIMULATIONS, JSON.stringify(data.savedSimulations));
  localStorage.setItem(STORAGE_KEYS.PREFERENCES, JSON.stringify(data.preferences));

  // Heating cost config (3 keys)
  // Merge fuel prices: keep user-modified, update non-modified to current defaults
  const mergedPrices = mergeFuelPrices(
    data.heatingCost.fuelPrices as FuelPrice[],
    getDefaultFuelPrices()
  );
  localStorage.setItem(STORAGE_KEY_PRICES, JSON.stringify(mergedPrices));
  localStorage.setItem(STORAGE_KEY_INVESTMENT, JSON.stringify(data.heatingCost.investmentCosts));
  localStorage.setItem(STORAGE_KEY_SETTINGS, JSON.stringify(data.heatingCost.settings));
}

// =============================================================================
// parseImportFile
// =============================================================================

/**
 * Reads a File, parses JSON, validates, and returns ImportResult.
 */
export async function parseImportFile(file: File): Promise<ImportResult> {
  try {
    const text = await file.text();
    if (!text.trim()) {
      return { success: false, errors: ['Plik jest pusty'], warnings: [] };
    }

    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      return { success: false, errors: ['Nieprawidłowy format JSON'], warnings: [] };
    }

    return validateExportData(parsed);
  } catch (error) {
    return {
      success: false,
      errors: [`Błąd odczytu pliku: ${error instanceof Error ? error.message : String(error)}`],
      warnings: [],
    };
  }
}
